import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Tooltip } from "react-tooltip";
import { useReactToPrint } from "react-to-print";
import { Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/react";
import {
	CheckBadgeIcon as CheckBadgeIconOutline,
	ClockIcon,
	ExclamationCircleIcon,
	ExclamationTriangleIcon,
	PrinterIcon,
} from "@heroicons/react/24/outline";
import { ChatBubbleLeftEllipsisIcon } from "@heroicons/react/20/solid";
import { BookOpenIcon, CheckBadgeIcon, ChevronDownIcon } from "@heroicons/react/24/solid";
import { Column } from "primereact/column";
import {
	DataTable,
	DataTableExpandedRows,
	DataTableRowEvent,
	DataTableValueArray,
	SortOrder,
} from "primereact/datatable";
import { scoreReportDraftAndPromptLink } from "../../../app/links";
import Avatar from "../../../components/Avatar";
import FormattedDateAndTime from "../../../components/FormattedDateAndTime";
import ProgressBar from "../../../components/ProgressBar";
import Select from "../../../components/input/Select";
import TextWithTip from "../../../components/TextWithTip";
import { CustomSortIcon } from "../../../components/DatatableHelpers";
import PrintCompletedPeerReviewsDocument from "../../peerReviews/PrintCompletedPeerReviewsDocument";
import {
	PeerReviewPrintViewModel,
	useLazyCompletedPeerReviewPrintQuery,
} from "../../peerReviews/peerReviewsApiSlice";
import {
	ScoreReportViewModel,
	useLazyPrintLastScoreReportsQuery,
} from "../../scoreReport/scoreReportApiSlice";
import PrintLastScoreReports from "../../scoreReport/PrintLastScoreReports";
import {
	CourseWritingActivityEssayModel,
	CourseWritingActivityPromptModel,
	DraftStatus,
	PrintPromptLastEssaysViewModel,
	useLazyPrintLastScoredEssaysQuery,
	WritingAction,
} from "../writingApiSlice";
import PrintLastScoredEssays from "./PrintLastScoredEssays";

interface CourseWritingActivityTableProps {
	activity: CourseWritingActivityPromptModel[];
	promptSelected?: number;
	sortField?: string;
	expandedStatusFilter: string;
	setSelectedPromptId: (id?: number) => void;
	setExpandedStatusFilter: (filter: string) => void;
}

interface CourseWritingActivityTableData {
	promptId: number;
	isAssigned: boolean;
	promptName: string;
	genreName: string;
	contentAreas: string;
	dueDate: string;
	lastUpdateDate?: string;
	reviewsCompletedCount: number;
	reviewsRequestedCount: number;
	newMessagesCount: number;
	totalMessagesCount: number;
	row: CourseWritingActivityPromptModel;
}

const mapPromptActivityData = (
	data: CourseWritingActivityPromptModel[]
): CourseWritingActivityTableData[] => {
	return data.map((row) => {
		return {
			promptId: row.promptId,
			isAssigned: row.isAssigned,
			promptName: row.promptName,
			genreName: row.genreName,
			contentAreas: row.contentAreas.join(", "),
			dueDate: row.dueDate,
			lastUpdateDate: row.lastUpdateDate,
			reviewsCompletedCount: row.reviewsCompletedCount,
			reviewsRequestedCount: row.reviewsRequestedCount,
			newMessagesCount: row.newMessagesCount,
			totalMessagesCount: row.totalMessagesCount,
			row: row,
		};
	});
};

export default function CourseWritingActivityTable(props: CourseWritingActivityTableProps) {
	const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows | DataTableValueArray>();

	const [sortField, setSortField] = useState<string>();
	const [sortOrder, setSortOrder] = useState<SortOrder>(0);

	const data = mapPromptActivityData(props.activity);

	useEffect(() => {
		setSortField(props.sortField);
		setSortOrder(1);
	}, [props.sortField]);

	useEffect(() => {
		if (!props.promptSelected) {
			setExpandedRows({});
		} else {
			expandRow();
		}
	}, [props.promptSelected]);

	useEffect(() => {
		if (props.promptSelected) {
			expandRow();
		}
	}, [props.activity]);

	function expandRow() {
		const promptId =
			props.activity.find((p) => p.promptId == props.promptSelected)?.promptId ?? 0;
		setExpandedRows({ [promptId]: true });
	}

	const onRowExpand = (event: any) => {
		// clicking on the row expander triggers a nested DataTableRowEvent
		const target =
			event.originalEvent.currentTarget ??
			(event.originalEvent.originalEvent.currentTarget as HTMLElement);

		if (target) {
			const table = target.closest("table");
			table &&
				[...table.getElementsByClassName("expanded")].forEach((e: HTMLElement) =>
					e.classList.remove("expanded")
				);

			const tr = target.closest("tr");
			tr && tr?.classList.add("expanded");
		}
		setExpandedRows({ [event.data.promptId]: true });
	};

	const onRowCollapse = (event: any) => {
		// clicking on the row expander triggers a nested DataTableRowEvent
		const target =
			event.originalEvent.currentTarget ??
			(event.originalEvent.originalEvent.currentTarget as HTMLElement);

		if (target) {
			const table = target.closest("table");
			table &&
				[...table.getElementsByClassName("expanded")].forEach((e: HTMLElement) =>
					e.classList.remove("expanded")
				);
		}
		setExpandedRows({});
		props.setSelectedPromptId(undefined);
		props.setExpandedStatusFilter("all");
	};

	const rowExpansionTemplate = (data: CourseWritingActivityTableData) => {
		return (
			<PromptExpandedTemplate
				activity={props.activity}
				promptSelected={data.promptId}
				statusFilter={props.expandedStatusFilter}></PromptExpandedTemplate>
		);
	};

	const onRowClick = (event: DataTableRowEvent) => {
		const xRows: any = expandedRows;

		if (xRows && xRows[event.data.promptId]) {
			onRowCollapse(event);
		} else {
			onRowExpand(event);
		}
	};

	const onSort = (event: any) => {
		setSortField(event.sortField);
		setSortOrder(event.sortOrder);
	};

	const assignedBodyTemplate = (data: CourseWritingActivityTableData) => {
		return (
			<span className="flex justify-center">
				{data.isAssigned && (
					<BookOpenIcon
						className="size-6"
						data-tooltip-id={`assigned-${data.promptId}-tooltip`}
					/>
				)}
				<Tooltip
					id={`assigned-${data.promptId}-tooltip`}
					place="top-start"
					content="Assignment"
				/>
			</span>
		);
	};

	const promptNameBodyTemplate = (data: CourseWritingActivityTableData) => {
		return (
			<div className="line-clamp-2 hover:line-clamp-none">
				{data.promptName.replace(/\//g, "/\u200b")}
			</div>
		);
	};

	const genreContentAreaBodyTemplate = (data: CourseWritingActivityTableData) => {
		return (
			<span>
				<span>{data.genreName.replace(/\//g, "/\u200b")}</span>
				<div className="mt-1 text-gray-500 text-sm !max-w-[172px] writing:!max-w-[225px]">
					<TextWithTip uniqueName={`content-${data.promptId}`} text={data.contentAreas} />
				</div>
			</span>
		);
	};

	const genreContentAreaSortFunction = (event: any) => {
		const data = [...event.data];
		data.sort((data1, data2) => {
			if (data1.genreName !== data2.genreName) {
				return (data1.genreName > data2.genreName ? 1 : -1) * event.order;
			}
			// If genre is the same, sort by content areas
			else {
				return (data1.contentAreas > data2.contentAreas ? 1 : -1) * event.order;
			}
		});
		return data;
	};

	const dueDateBodyTemplate = (data: CourseWritingActivityTableData) => {
		return <span>{data.dueDate ? <FormattedDateAndTime date={data.dueDate} /> : "-"}</span>;
	};

	const statusBodyTemplate = (data: CourseWritingActivityTableData) => {
		return (
			<div>
				<StatusColumnTemplate row={data.row} />
			</div>
		);
	};

	const updateDateBodyTemplate = (data: CourseWritingActivityTableData) => {
		return (
			<span>
				{data.lastUpdateDate ? <FormattedDateAndTime date={data.lastUpdateDate} /> : "-"}
			</span>
		);
	};

	const reviewsBodyTemplate = (data: CourseWritingActivityTableData) => {
		return data.reviewsRequestedCount == 0 ? (
			<span>-</span>
		) : (
			<span className="tracking-widest">
				<span className="text-lg font-medium">{data.reviewsCompletedCount}</span>/
				<span className="text-sm">{data.reviewsRequestedCount}</span>
			</span>
		);
	};

	const reviewsSortFunction = (event: any) => {
		const data = [...event.data];
		data.sort((data1, data2) => {
			if (data2.reviewsCompletedCount !== data1.reviewsCompletedCount) {
				return (data2.reviewsCompletedCount - data1.reviewsCompletedCount) * event.order;
			}
			// If completed counts are equal, sort by requested count
			else {
				return (data2.reviewsRequestedCount - data1.reviewsRequestedCount) * event.order;
			}
		});
		return data;
	};

	const messagesBodyTemplate = (data: CourseWritingActivityTableData) => {
		return data.totalMessagesCount == 0 ? (
			<span>-</span>
		) : (
			<span className="relative inline-block align-middle">
				<ChatBubbleLeftEllipsisIcon className="size-7" />
				{data.newMessagesCount > 0 && (
					<span
						id="notification-badge"
						className="absolute rounded-full leading-5 bg-green-200 size-5 text-xs text-center text-green-800 font-medium left-4 bottom-4">
						{data.newMessagesCount}
					</span>
				)}
			</span>
		);
	};

	const messagesSortFunction = (event: any) => {
		const data = [...event.data];
		data.sort((data1, data2) => {
			if (data2.newMessagesCount !== data1.newMessagesCount) {
				return (data2.newMessagesCount - data1.newMessagesCount) * event.order;
			}
			// If new messages counts is equal, sort by total messages count
			else {
				return (data2.totalMessagesCount - data1.totalMessagesCount) * event.order;
			}
		});
		return data;
	};

	return (
		<DataTable
			value={data}
			dataKey="promptId"
			stripedRows
			sortIcon={CustomSortIcon}
			sortField={sortField}
			sortOrder={sortOrder}
			onSort={onSort}
			expandedRows={expandedRows}
			onRowClick={onRowClick}
			onRowExpand={onRowExpand}
			onRowCollapse={onRowCollapse}
			rowExpansionTemplate={rowExpansionTemplate}
			selectionMode="single"
			emptyMessage="No results found"
			className="tbl tbl-data tbl-datatable table-fixed min-w-[40rem] cursor-default"
			style={{ marginTop: "16px" }}>
			<Column
				expander={true}
				headerClassName="!pl-3 !pr-0.5"
				className="!pl-3 !pr-0.5"></Column>
			<Column
				header="Assigned"
				body={assignedBodyTemplate}
				headerClassName="!px-0"
				className="!px-0"
				sortable
				sortField="isAssigned"></Column>
			<Column
				header="Prompt"
				body={promptNameBodyTemplate}
				headerClassName="!px-2 writing:!p-4 writing:min-w-[260px]"
				className="!px-2 writing:!p-4"
				sortable
				sortField="promptName"></Column>
			<Column
				header="Genre / Content Area(s)"
				field="genreName"
				body={genreContentAreaBodyTemplate}
				headerClassName="!px-2 break-words !max-w-[172px] writing:!min-w-[225px] writing:!max-w-[225px] writing:!p-4"
				className="!px-2 writing:!p-4 !max-w-[172px] writing:!w-[225px]"
				sortable
				sortFunction={genreContentAreaSortFunction}></Column>
			<Column
				header="Due Date"
				body={dueDateBodyTemplate}
				headerClassName="!px-2 writing:!p-4"
				className="!px-2 writing:!p-4 writing:min-w-44"
				sortable
				sortField="dueDate"></Column>
			<Column
				header="Status"
				body={statusBodyTemplate}
				headerClassName="writing:w-[266px] !px-1"
				className="!px-1"></Column>
			<Column
				header="Recent Activity"
				body={updateDateBodyTemplate}
				headerClassName="!px-2 writing:!p-4"
				className="!px-2 writing:!p-4 writing:min-w-44"
				sortable
				sortField="lastUpdateDate"></Column>
			<Column
				header="Reviews"
				field="reviewsCompletedCount"
				body={reviewsBodyTemplate}
				headerClassName="!px-2"
				className="!px-2 !text-center"
				sortable
				sortFunction={reviewsSortFunction}></Column>
			<Column
				header="Messages"
				field="newMessagesCount"
				body={messagesBodyTemplate}
				headerClassName="!px-2 writing:!pr-4 w-[70px]"
				className="!px-2 writing:!pr-4 !text-center"
				sortable
				sortFunction={messagesSortFunction}></Column>
		</DataTable>
	);
}

interface PromptExpandedProps {
	activity: CourseWritingActivityPromptModel[];
	promptSelected: number;
	statusFilter: string;
}

interface PromptExpandedData extends CourseWritingActivityEssayModel {
	key: string;
	writerFormalName: string;
	lastUpdateDate?: string;
	score?: number;
	reviewsCompletedCount: number;
	reviewsRequestedCount: number;
	newMessagesCount: number;
	totalMessagesCount: number;
}

const mapPromptExpandedData = (data: CourseWritingActivityEssayModel[]): PromptExpandedData[] => {
	return data.map((row) => {
		return {
			...row,
			key: row.writerId + "-" + row.essayId,
			writerFormalName: row.writerFormalName,
			lastUpdateDate: row.lastUpdateDate,
			score: row.score,
			reviewsCompletedCount: row.reviewsCompletedCount,
			reviewsRequestedCount: row.reviewsRequestedCount,
			newMessagesCount: row.newMessagesCount,
			totalMessagesCount: row.totalMessagesCount,
		};
	});
};

function PromptExpandedTemplate(props: PromptExpandedProps) {
	const essaysForPrompt = props.activity.find((p) => p.promptId === props.promptSelected)?.essays;
	const maxScore = props.activity.find((p) => p.promptId === props.promptSelected)?.genreMaxScore;

	const [data, setData] = useState<PromptExpandedData[]>();
	const [idsWithMultipleEssays, setIdsWithMultipleEssays] = useState<number[]>([]);
	const [filteredData, setFilteredData] = useState<PromptExpandedData[]>();
	const [statusFilter, setStatusFilter] = useState(props.statusFilter);

	// const [allRowsLength, setAllRowsLength] = useState(0);
	// const [checkedIds, setCheckedIds] = useState<number[]>([]);
	// const [allChecked, setAllChecked] = useState(false);
	// const [allFilteredCheckboxIds, setAllFilteredCheckboxIds] = useState<number[]>([]);
	// const [allFilteredChecked, setAllFilteredChecked] = useState(false);

	const printLastScoredEssaysRef = useRef<HTMLDivElement>(null);
	const printLastScoredEssays = useReactToPrint({ contentRef: printLastScoredEssaysRef });
	const [printableEssaysForPrompt, setPrintableEssaysForPrompt] =
		useState<PrintPromptLastEssaysViewModel>();

	const printLastScoreReportsRef = useRef<HTMLDivElement>(null);
	const printLastScoreReports = useReactToPrint({ contentRef: printLastScoreReportsRef });
	const [printableScoreReportsForPrompt, setPrintableScoreReportsForPrompt] =
		useState<ScoreReportViewModel[]>();

	const printReviewsContentRef = useRef<HTMLDivElement>(null);
	const printPeerReviews = useReactToPrint({ contentRef: printReviewsContentRef });
	const [completedReviews, setCompletedReviews] = useState<PeerReviewPrintViewModel[]>();

	const [callGetPrintableLastEssays] = useLazyPrintLastScoredEssaysQuery();
	const [callGetPrintableScoreReports] = useLazyPrintLastScoreReportsQuery();
	const [callGetCompletedReviews] = useLazyCompletedPeerReviewPrintQuery();

	const navigate = useNavigate();

	useEffect(() => {
		setData(mapPromptExpandedData(essaysForPrompt ?? []));
	}, [essaysForPrompt]);

	useEffect(() => {
		// const _temp: number[] = [];

		// data?.map((row) => {
		// 	if (row.essayId != 0) _temp.push(row.essayId);
		// });
		// setAllFilteredCheckboxIds(_temp);
		// setAllRowsLength(_temp?.length ?? 0);

		var essayWriterIds = data?.map((e) => e.writerId) ?? [];
		var duplicates = essayWriterIds.filter(
			(value, index) =>
				essayWriterIds.indexOf(value) !== index &&
				essayWriterIds.lastIndexOf(value) === index
		);
		setIdsWithMultipleEssays(duplicates);
	}, [data]);

	useEffect(() => {
		if (data) {
			if (statusFilter === "in progress")
				setFilteredData(
					data.filter(
						(e) =>
							e.status === DraftStatus.Incomplete ||
							e.status === DraftStatus.ScoringFailed
					)
				);
			else if (statusFilter === "complete")
				setFilteredData(
					data.filter(
						(e) => e.status === DraftStatus.Finalized || e.status === DraftStatus.Scored
					)
				);
			else if (statusFilter === "not started")
				setFilteredData(data.filter((e) => e.status === DraftStatus.NotStarted));
			else setFilteredData(data);
		}
	}, [statusFilter, data]);

	function handlePrintLastEssays() {
		setPrintableEssaysForPrompt(undefined);
		callGetPrintableLastEssays(props.promptSelected)
			.unwrap()
			.then((payload) => {
				setPrintableEssaysForPrompt(payload);
			});
	}

	function handlePrintLastScoreReports() {
		setPrintableScoreReportsForPrompt(undefined);
		callGetPrintableScoreReports(props.promptSelected)
			.unwrap()
			.then((payload) => {
				setPrintableScoreReportsForPrompt(payload);
			});
	}

	function handlePrintPeerReviews(essayId: number) {
		setCompletedReviews(undefined);
		callGetCompletedReviews(essayId)
			.unwrap()
			.then((payload) => {
				setCompletedReviews(payload.model);
			});
	}

	useEffect(() => {
		if (printableEssaysForPrompt && printableEssaysForPrompt.essays.length > 0)
			printLastScoredEssays();
	}, [printableEssaysForPrompt]);

	useEffect(() => {
		if (printableScoreReportsForPrompt && printableScoreReportsForPrompt.length > 0)
			printLastScoreReports();
	}, [printableScoreReportsForPrompt]);

	useEffect(() => {
		if (completedReviews && completedReviews.length > 0) printPeerReviews();
	}, [completedReviews]);

	// useEffect(() => {
	// 	const _temp: number[] = [];

	// 	filteredData?.map((row) => {
	// 		if (row.essayId != 0) _temp.push(row.essayId);
	// 	});
	// 	setAllFilteredCheckboxIds(_temp);
	// }, [filteredData]);

	// useEffect(() => {
	// 	if (checkedIds.length === allRowsLength && allRowsLength != 0) {
	// 		setAllChecked(true);
	// 	} else {
	// 		setAllChecked(false);
	// 	}

	// 	updateSelectAllCheckbox();
	// }, [checkedIds]);

	// useEffect(() => {
	// 	updateSelectAllCheckbox();
	// }, [allFilteredCheckboxIds]);

	// function updateSelectAllCheckbox() {
	// 	if (
	// 		allFilteredCheckboxIds.length > 0 &&
	// 		allFilteredCheckboxIds.every((id) => checkedIds.includes(id))
	// 	) {
	// 		setAllFilteredChecked(true);
	// 	} else {
	// 		setAllFilteredChecked(false);
	// 	}
	// }

	// function handleCheckboxChange(e: ChangeEvent<HTMLInputElement>) {
	// 	e.preventDefault();
	// 	if (e.target.checked) {
	// 		setCheckedIds((prevCheckedIds) => [...prevCheckedIds, Number(e.target.value)]);
	// 		//setCheckedIds([...checkedIds, Number(e.target.value)]);
	// 	} else {
	// 		setCheckedIds(checkedIds.filter((item) => item !== Number(e.target.value)));
	// 	}
	// }

	// function handleSelectAll(e: ChangeEvent<HTMLInputElement>) {
	// 	e.preventDefault();
	// 	if (e.target.checked) {
	// 		const idsToAdd: number[] = [];
	// 		allFilteredCheckboxIds.map((id) => {
	// 			if (!checkedIds.includes(id)) {
	// 				idsToAdd.push(id);
	// 			}
	// 		});
	// 		setCheckedIds([...checkedIds, ...idsToAdd]);
	// 	} else {
	// 		setCheckedIds(checkedIds.filter((item) => !allFilteredCheckboxIds.includes(item)));
	// 	}
	// }

	// const checkboxHeaderTemplate = () => {
	// 	return (
	// 		<input
	// 			id="selectAllCheckbox"
	// 			type="checkbox"
	// 			disabled={allRowsLength === 0 || allFilteredCheckboxIds.length == 0}
	// 			className="size-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600 disabled:cursor-not-allowed"
	// 			onChange={(e) => handleSelectAll(e)}
	// 			checked={allChecked || allFilteredChecked}
	// 		/>
	// 	);
	// };

	// const checkboxBodyTemplate = (row: PromptExpandedData) => {
	// 	return row.essayId != 0 ? (
	// 		<input
	// 			id={`checkbox-${row.key}`}
	// 			value={row.essayId}
	// 			type="checkbox"
	// 			checked={checkedIds.includes(row.essayId)}
	// 			className="size-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
	// 			onChange={(e) => handleCheckboxChange(e)}
	// 		/>
	// 	) : (
	// 		<></>
	// 	);
	// };

	const lastUpdatedBodyTemplate = (row: PromptExpandedData) => {
		return row.lastUpdateDate ? (
			<span>
				<FormattedDateAndTime date={row.lastUpdateDate} />
			</span>
		) : (
			<span>-</span>
		);
	};

	const avatarBodyTemplate = (row: PromptExpandedData) => {
		return (
			<Avatar
				initials={row.writerInitials}
				className="!size-8"
				hasImage={row.writerHasImage}
				userId={row.writerId}
			/>
		);
	};

	const studentBodyTemplate = (row: PromptExpandedData) => {
		return <TextWithTip uniqueName={`name-${row.writerId}`} text={row.writerFormalName} />;
	};

	const statusIconBodyTemplate = (row: PromptExpandedData) => {
		return (
			<div>
				{row.status === DraftStatus.Finalized ? (
					<CheckBadgeIcon
						className="size-5 text-green-700"
						aria-hidden="true"
						data-tooltip-id="completed-tooltip"
					/>
				) : row.status === DraftStatus.Scored ? (
					<CheckBadgeIconOutline
						className="size-5 text-green-700"
						aria-hidden="true"
						data-tooltip-id="completed-tooltip"
					/>
				) : row.status === DraftStatus.ScoringFailed ||
				  row.status === DraftStatus.Incomplete ? (
					<ClockIcon
						className="size-5 text-yellow-500"
						aria-hidden="true"
						data-tooltip-id="in-progress-tooltip"
					/>
				) : (
					<ExclamationTriangleIcon
						className="size-5 text-red-500"
						aria-hidden="true"
						data-tooltip-id="not-started-tooltip"
					/>
				)}
				<Tooltip id="completed-tooltip" place="top" content="Completed" />
				<Tooltip id="in-progress-tooltip" place="top" content="In Progress" />
				<Tooltip id="not-started-tooltip" place="top" content="Not Started" />
			</div>
		);
	};

	const scoreBodyTemplate = (row: PromptExpandedData) => {
		return !row.score ? (
			<span>-</span>
		) : (
			<span className="tracking-widest">
				<span className="text-lg font-medium">{row.score}</span>/
				<span className="text-sm">{maxScore}</span>
			</span>
		);
	};

	const reviewsBodyTemplate = (row: PromptExpandedData) => {
		return row.reviewsRequestedCount == 0 ? (
			<span>-</span>
		) : (
			<span className="tracking-widest">
				{row.reviewsCompletedCount > 0 ? (
					<span
						className="text-lg font-medium link"
						onClick={() => handlePrintPeerReviews(row.essayId)}>
						{row.reviewsCompletedCount}
					</span>
				) : (
					<span className="text-lg font-medium">{row.reviewsCompletedCount}</span>
				)}
				/<span className="text-sm">{row.reviewsRequestedCount}</span>
			</span>
		);
	};

	const reviewsSortFunction = (event: any) => {
		const data = [...event.data];
		data.sort((data1, data2) => {
			if (data2.reviewsCompletedCount !== data1.reviewsCompletedCount) {
				return (data2.reviewsCompletedCount - data1.reviewsCompletedCount) * event.order;
			}
			// If completed counts are equal, sort by requested count
			else {
				return (data2.reviewsRequestedCount - data1.reviewsRequestedCount) * event.order;
			}
		});
		return data;
	};

	const messagesBodyTemplate = (row: PromptExpandedData) => {
		return row.totalMessagesCount == 0 ? (
			<span>-</span>
		) : (
			<span className="tracking-widest">
				<span
					className="text-lg font-medium link"
					onClick={() =>
						navigate(
							scoreReportDraftAndPromptLink(props.promptSelected, row.actionId),
							{
								state: { section: "EssayMessages" },
							}
						)
					}>
					{row.newMessagesCount}
				</span>{" "}
				new/
				<span className="text-sm">{row.totalMessagesCount}</span>
			</span>
		);
	};

	const messagesSortFunction = (event: any) => {
		const data = [...event.data];
		data.sort((data1, data2) => {
			if (data2.newMessagesCount !== data1.newMessagesCount) {
				return (data2.newMessagesCount - data1.newMessagesCount) * event.order;
			}
			// If new messages counts is equal, sort by total messages count
			else {
				return (data2.totalMessagesCount - data1.totalMessagesCount) * event.order;
			}
		});
		return data;
	};

	const duplicateBodyTemplate = (row: PromptExpandedData) => {
		return idsWithMultipleEssays.includes(row.writerId) ? (
			<span className="flex justify-center">
				<ExclamationCircleIcon
					className="size-5 text-red-500"
					data-tooltip-id={`duplicate-${row.essayId}-${row.writerId}-tooltip`}
				/>
				<Tooltip
					id={`duplicate-${row.essayId}-${row.writerId}-tooltip`}
					place="top-start"
					content="This student has more than one essay for this prompt."
				/>
			</span>
		) : (
			<></>
		);
	};

	const actionBodyTemplate = (row: PromptExpandedData) => {
		return row.action != WritingAction.NotStarted ? (
			<button
				id={`essay-${row.essayId}`}
				className="btn btn-action !px-2"
				onClick={() =>
					navigate(scoreReportDraftAndPromptLink(props.promptSelected, row.actionId), {
						state: {
							section:
								row.action === WritingAction.Organize ? "OrganizerSection" : null,
						},
					})
				}>
				View
			</button>
		) : (
			<></>
		);
	};

	return (
		<div className="-mx-4 -my-3 px-4 pb-3 bg-gray-100">
			<div className="mx-2 xl:mx-8 pt-1 flex flex-row relative justify-between items-end">
				<Select
					value={statusFilter}
					setValue={(v) => setStatusFilter(v as string)}
					label="Status"
					simpleListBoxId="statusFilter"
					options={[
						{ value: "all", label: "All" },
						{
							value: "not started",
							label: "Not Started",
						},
						{
							value: "in progress",
							label: "In Progress",
						},
						{ value: "complete", label: "Complete" },
					]}
					simpleListBoxClasses="w-64"
				/>
				{data?.some((e) => e.printable) && (
					<PrintDropdown
						handlePrintLastEssays={handlePrintLastEssays}
						handlePrintScoreReports={handlePrintLastScoreReports}
					/>
				)}
			</div>
			<DataTable
				value={filteredData}
				dataKey="key"
				loading={filteredData == undefined}
				emptyMessage="No matching results found"
				stripedRows
				sortIcon={CustomSortIcon}
				className="tbl !w-auto mt-2 mx-2 xl:mx-8">
				{/* <Column
					header={checkboxHeaderTemplate}
					body={checkboxBodyTemplate}
					headerClassName="w-4 !px-2 writing:!pl-4"
					className="w-4 !px-2 writing:!pl-4"></Column> */}
				<Column
					body={avatarBodyTemplate}
					headerClassName="hidden xl:table-cell !pl-2 !pr-0 w-8"
					className="hidden xl:table-cell !pl-2 !pr-0"></Column>
				<Column
					header="Student"
					body={studentBodyTemplate}
					headerClassName="!max-w-48 !px-2 writing:!p-4 writing:!max-w-[300px]"
					className="!max-w-48 !px-2 writing:!p-4 writing:!max-w-[300px]"
					sortable
					sortField="writerFormalName"></Column>
				<Column
					header="Last Updated"
					body={lastUpdatedBodyTemplate}
					headerClassName="!px-2 writing:!p-4"
					className="!px-2 writing:!p-4 writing:min-w-44"
					sortable
					sortField="lastUpdateDate"></Column>
				<Column
					body={statusIconBodyTemplate}
					headerClassName="!px-0 w-4"
					className="!px-0"></Column>
				<Column
					header="Status"
					field="statusDescription"
					headerClassName="!pl-2"
					className="!pl-2"></Column>
				<Column
					header="Stage"
					field="step"
					headerClassName="!px-2 writing:!p-4"
					className="!px-2 writing:!p-4"></Column>
				<Column
					header="Score"
					body={scoreBodyTemplate}
					headerClassName="!px-2 writing:!p-4"
					className="!px-2 writing:!p-4"
					sortable
					sortField="score"></Column>
				<Column
					header="Reviews"
					field="reviewsCompletedCount"
					body={reviewsBodyTemplate}
					headerClassName="!px-2 writing:!p-4"
					className="!px-2 writing:!p-4"
					sortable
					sortFunction={reviewsSortFunction}></Column>
				<Column
					header="Messages"
					field="newMessagesCount"
					body={messagesBodyTemplate}
					headerClassName="!px-2 writing:!p-4"
					className="!px-2 writing:!p-4"
					sortable
					sortFunction={messagesSortFunction}></Column>
				<Column
					body={duplicateBodyTemplate}
					headerClassName={`${idsWithMultipleEssays.length === 0 ? "hidden" : ""} !px-0 w-4`}
					className={`${idsWithMultipleEssays.length === 0 ? "hidden" : ""} !px-0`}></Column>
				<Column
					body={actionBodyTemplate}
					headerClassName="!px-2 w-16"
					className="!px-2"></Column>
			</DataTable>
			<div ref={printLastScoredEssaysRef} className="hidden print:block">
				<PrintLastScoredEssays promptEssays={printableEssaysForPrompt} />
			</div>
			<div ref={printLastScoreReportsRef} className="hidden print:block">
				<PrintLastScoreReports scoreReports={printableScoreReportsForPrompt} />
			</div>
			<div ref={printReviewsContentRef} className="hidden print:block">
				<PrintCompletedPeerReviewsDocument completedReviews={completedReviews ?? []} />
			</div>
		</div>
	);
}

interface StatusColumnTemplateProps {
	row: CourseWritingActivityPromptModel;
}

function StatusColumnTemplate({ row }: StatusColumnTemplateProps) {
	return row.totalCount === 0 ? (
		<></>
	) : row.completed ? (
		<div className="items-center text-green-700 inline-flex float-right">
			<CheckBadgeIcon className="mr-1.5 h-7 w-7" aria-hidden="true" />
			<p>
				<strong>
					{row.totalCount}
					<span className="text-xs">/{row.totalCount}</span>
				</strong>{" "}
				complete!
			</p>
		</div>
	) : row.scoredCount == 0 ? (
		<div>
			<div className="w-full">
				<ProgressBar
					id={row.promptId}
					started={row.startedCount}
					showStartedOnly={true}
					assigned={row.totalCount}
					barColor="bg-zinc-400"
				/>
				<p className="text-gray-500 text-sm text-right pt-0.5">
					<strong>
						{row.startedCount}
						<span className="text-xs">/{row.totalCount}</span>
					</strong>{" "}
					students have started
				</p>
			</div>
		</div>
	) : (
		<div>
			<div className="w-full">
				<ProgressBar
					id={row.promptId}
					started={row.startedCount}
					completed={row.scoredCount}
					assigned={row.totalCount}
					completedLabel="Last Draft Scored"
				/>
				<p className="text-gray-500 text-sm text-right pt-0.5">
					<strong>
						{row.scoredCount}
						<span className="text-xs">/{row.totalCount}</span>
					</strong>{" "}
					students with last draft scored
				</p>
			</div>
		</div>
	);
}

interface PrintDropdownProps {
	handlePrintLastEssays: () => void;
	handlePrintScoreReports: () => void;
}

function PrintDropdown(props: PrintDropdownProps) {
	return (
		<Menu as="div">
			<MenuButton id="print-options" className="btn btn-neutral">
				<div className="flex items-center">
					<PrinterIcon className="size-4 mr-2" aria-hidden="true" />
					Print
					<ChevronDownIcon className="size-4 ml-2" aria-hidden="true" />
				</div>
			</MenuButton>
			<MenuItems
				anchor="bottom end"
				className="absolute z-10 pb-1 rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none hover:cursor-pointer">
				<MenuItem key="last-essays">
					<a
						onClick={props.handlePrintLastEssays}
						className="block data-[focus]:bg-gray-100 px-4 py-2 text-sm text-gray-700">
						<div className="flex items-center">
							<PrinterIcon className="size-4 mr-2" aria-hidden="true" />
							Print Last Scored Essays
						</div>
					</a>
				</MenuItem>
				<MenuItem key="last-score-reports">
					<a
						onClick={props.handlePrintScoreReports}
						className="block data-[focus]:bg-gray-100 px-4 py-2 text-sm text-gray-700">
						<div className="flex items-center">
							<PrinterIcon className="size-4 mr-2" aria-hidden="true" />
							Print Last Score Reports
						</div>
					</a>
				</MenuItem>
			</MenuItems>
		</Menu>
	);
}
